import { useAuth } from "react-oidc-context";
import Input from "../components/Input";
import LoginLayout from "../layouts/LoginLayout";
import { useEffect, useState } from "react";
import { useAuthContext } from "../providers/AuthProvider";
import { FormDataType } from "../helpers/types";
import { getCityFromPostCode } from "../helpers/utils";
import { getUserSchema } from "../helpers/schema";
import { useTranslation } from "react-i18next";

const FirstLogin = () => {
    const auth = useAuth();
    const { t } = useTranslation();
    const { user, apiCall, setUserData } = useAuthContext();
    const [isValid, setValid] = useState<boolean>(false);
    const [isSaving, setSaving] = useState<boolean>(false);
    const [formData, setFormData] = useState<FormDataType | null>(null);
    const [errors, setErrors] = useState<FormDataType>({});

    // Validation of the form
    const Information = getUserSchema(t);
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSaving(true);

        const valid = Information.safeParse(formData);
        if (valid.success) {
            try {
                const response = await apiCall({
                    action: 'saveUserData',
                    data: formData,
                    auth: auth?.user?.profile
                });

                if (response.data.status === 200) {
                    setUserData(response.data.data);
                }
            } catch (error) {
                console.error("saveUserData ERROR: ", error);
            } finally {
                setSaving(false);
            }
        }
    }

    const handleInputChange = async (value: string, name: keyof typeof Information.shape) => {
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        const inputSchema = Information.shape[name];
        if (inputSchema) {
            const result = inputSchema.safeParse(value);
            if (!result.success) {
                // Add the error to the errors object
                updateErrors(name, result.error.errors[0]?.message || 'Noe er feil');
            } else {
                // Remove the error from the errors object
                updateErrors(name, null);
            }
        }


        if (name === 'zip') {
            if (value.length === 4) {
                const response = await getCityFromPostCode(value);
                setFormData((prevData) => ({
                    ...prevData,
                    city: response
                }));
            } else {
                setFormData((prevData) => ({
                    ...prevData,
                    city: ''
                }));
            }
        }
    };

    const updateErrors = (field: string, value: string | number | null) => {
        setErrors((prev) => {
            if (value === null) {
                // If value is null, remove the field from the state object
                const { [field]: _, ...newErrorsData } = prev;
                return newErrorsData;
            }

            // Otherwise, update the state as usual
            return {
                ...prev,
                [field]: value,
            };
        });
    }

    useEffect(() => {
        const valid = Information.safeParse(formData);
        if (valid.success) {
            setValid(true);
        } else {
            setValid(false);
        }
    }, [formData]);

    return (
        <LoginLayout
            title={`Hei ${auth?.user?.profile?.given_name}!`}
            subtitle="Vi trenger litt mer informasjon om deg før vi kan gå i gang."
        >
            <form onSubmit={handleSubmit} className="text-start">
                <div className="mb-4" style={{ maxWidth: '630px' }}>
                    <div className="row gy-4">
                        <Input
                            value={formData?.email || ''}
                            onChange={(value) => handleInputChange(value, 'email')}
                            col="col-lg-8"
                            label={t('input_email_label')}
                            placeholder={t('input_email_placeholder')}
                            name="email"
                            type="text"
                            defaultValue={user?.email || ""}
                            error={errors.email}
                            required
                            rules={{}}
                        />
                        <Input
                            value={formData?.phone || ''}
                            onChange={(value) => handleInputChange(value, 'phone')}
                            col="col-lg-4"
                            label={t('input_phone_label')}
                            placeholder={t('input_phone_placeholder')}
                            name="phone"
                            type="text"
                            defaultValue={user?.phone || ""}
                            error={errors.phone}
                            required
                            rules={{}}
                        />
                        <Input
                            value={formData?.address || ''}
                            onChange={(value) => handleInputChange(value, 'address')}
                            col="col-12"
                            label={t('input_address_label')}
                            placeholder={t('input_address_placeholder')}
                            name="address"
                            type="text"
                            defaultValue={user?.address || ""}
                            error={errors.address}
                            required
                            rules={{}}
                        />
                        <Input
                            value={formData?.zip || ''}
                            onChange={(value) => handleInputChange(value, 'zip')}
                            col="col-lg-3"
                            label={t('input_zip_label')}
                            placeholder={t('input_zip_placeholder')}
                            name="zip"
                            type="text"
                            defaultValue={user?.zip || ""}
                            error={errors.zip}
                            required
                            rules={{}}
                        />
                        <Input
                            value={formData?.city || ''}
                            col="col-lg-9"
                            label={t('input_city_label')}
                            placeholder={t('input_city_placeholder')}
                            name="city"
                            type="text"
                            defaultValue={user?.city || ""}
                            disabled
                            error={errors.city}
                            required
                            rules={{}}
                        />
                    </div>
                </div>
                <div className="text-center">
                    <button
                        className="btn btn-primary btn-lg rounded-pill w-auto"
                        disabled={!isValid || isSaving}
                    >
                        {t('next')}
                    </button>
                </div>

            </form>
        </LoginLayout>
    )
}

export default FirstLogin;